import { type ApiCreateOrg, type ApiOrg, type ApiUpdateOrg, getCurrentOrgQueryKey } from '@kaiizen/kai-pro-api';
import { QueryCache, TypedCacheEntry, useCachedQuery, useQueryCacheMutation } from '@kaiizen/react-persistent-cache';
import { useAuth } from '../useAuth';
import { useKaiProApiClient } from '../useKaiProApiClient';
import { useCurrentOrg } from './useCurrentOrg';

const ALL_ORGS_QUERY_KEY = 'allOrgs';

const getUserOrgsQueryKey = (userId: string | undefined) => `orgs/${userId}`;

/** Merges `org` into the cached list at `queryKey`, keeping fields only the cached copy has (a member org's role). */
const updateOrgInCachedList = async (queryCache: QueryCache, queryKey: string, org: ApiOrg) => {
  const existing = await queryCache.cache.getEntry(queryKey) as TypedCacheEntry<ApiOrg[]> | undefined;
  if (existing) queryCache.cache.set(queryKey, existing.value.map(cachedOrg => cachedOrg.id === org.id ? { ...cachedOrg, ...org } : cachedOrg));
};

export const useOrgs = () => {
  const { user } = useAuth();
  const kaiProApiClient = useKaiProApiClient();

  const query = useCachedQuery(
    getUserOrgsQueryKey(user?.id),
    () => kaiProApiClient.getOrgsByUserId(user!.id),
    { enabled: !!kaiProApiClient.token && !!user?.id },
  );

  return { orgs: query.data || [] };
};

export const useAllOrgs = () => {
  const kaiProApiClient = useKaiProApiClient();

  const query = useCachedQuery(
    ALL_ORGS_QUERY_KEY,
    () => kaiProApiClient.getAllOrgs(),
    { enabled: !!kaiProApiClient.token },
  );

  return { allOrgs: query.data || [] };
};

/**
 * Super admin only: creates an org and adds it to the cached `useAllOrgs` list, so org pickers see it immediately.
 * Never retried: creating an org is not idempotent, so a retry after a lost response would make a second org.
 *
 * @example
 * ```ts
 * const createOrg = useCreateOrg();
 * const org = await createOrg.mutate({ name: 'Acme Rentals', isLive: false });
 * ```
 */
export const useCreateOrg = () => {
  const kaiProApiClient = useKaiProApiClient();
  return useQueryCacheMutation(
    (payload: ApiCreateOrg) => kaiProApiClient.createOrg(payload),
    {
      maxRetryMs: 0,
      onSuccess: async (org, queryCache) => {
        const existing = await queryCache.cache.getEntry(ALL_ORGS_QUERY_KEY) as TypedCacheEntry<ApiOrg[]> | undefined;
        if (existing) queryCache.cache.set(ALL_ORGS_QUERY_KEY, [...existing.value.filter(cachedOrg => cachedOrg.id !== org.id), org]);
      },
    },
  );
};

/**
 * Updates the current org, and every cached copy of it: `useCurrentOrg`, `useOrgs` and `useAllOrgs`.
 * Only super admins can change `isLive`.
 *
 * @example
 * ```ts
 * const updateCurrentOrg = useUpdateCurrentOrg();
 * await updateCurrentOrg.mutate({ name: 'Acme Rentals' });
 * ```
 */
export const useUpdateCurrentOrg = () => {
  const kaiProApiClient = useKaiProApiClient();
  const { user } = useAuth();
  const { currentOrg } = useCurrentOrg();
  return useQueryCacheMutation(
    (payload: ApiUpdateOrg) => kaiProApiClient.updateOrg(currentOrg!.id, payload),
    {
      onSuccess: async (org, queryCache) => {
        queryCache.cache.set(getCurrentOrgQueryKey(kaiProApiClient.token), { ...currentOrg, ...org });
        await updateOrgInCachedList(queryCache, getUserOrgsQueryKey(user?.id), org);
        await updateOrgInCachedList(queryCache, ALL_ORGS_QUERY_KEY, org);
      },
    },
  );
};
